import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFilterProducts } from '../../../api/apiMultiFilter';
import ProductGrid from './components/ProductGrid';
import FilterSelection from './components/FilterSelection';
import ApplicationSelection from './components/ApplicationSelection';
import ClearFilters from './components/ClearFilters';
import FilterTags from './components/FilterTags';
import { Grid, Box, Typography, Container } from '@mui/material';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

// 🔥 IMPORT SHARED TYPES - Remove duplicate interfaces
import {
    Product,
    FiltersData,
    MultiFilterApiResponse,
    MultiFilterMutationRequest,
    MultiFilterMutation
} from '../../../types/Multifilter.types';

const getDefaultApplications = (pathname: string): string[] => {
    const pathParts = pathname.split('/');
    const applicationIndex = pathParts.indexOf('application');

    if (applicationIndex === -1) return [];

    const applicationPath = decodeURIComponent(pathParts[applicationIndex + 1]);

    // Map of path values to their corresponding default applications
    const applicationDefaults: Record<string, string[]> = {
        'curtaining': ['Curtaining', 'Dual Purpose'],
        'dual purpose': ['Dual Purpose'],
        'upholstery': ['Upholstery', 'Dual Purpose']
    };

    return applicationDefaults[applicationPath.toLowerCase()] || [];
};

function FilterComponent({ itemGroup, title }: any) {
    const navigate = useNavigate();
    const location = useLocation();

    const abortControllerRef = useRef<AbortController | null>(null);

    const [defaultApplications, setDefaultApplications] = useState<string[]>([]);

    // Application states
    const [availableApplications, setAvailableApplications] = useState<string[]>([]);
    const [applicationsList, setApplicationsList] = useState<string[]>([]);

    // Available option states
    const [availableColours, setAvailableColours] = useState<string[]>([]);
    const [availableDesigns, setAvailableDesigns] = useState<string[]>([]);
    const [availableTypes, setAvailableTypes] = useState<string[]>([]);
    const [availableBrands, setAvailableBrands] = useState<string[]>([]);
    const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
    const [availableStandards, setAvailableStandards] = useState<string[]>([]);
    const [availableSizes, setAvailableSizes] = useState<string[]>([]);
    const [availableShapes, setAvailableShapes] = useState<string[]>([]);

    // Selected value states
    const [colourList, setColourList] = useState<string[]>([]);
    const [designList, setDesignList] = useState<string[]>([]);
    const [typesList, setTypesList] = useState<string[]>([]);
    const [brandList, setBrandList] = useState<string[]>([]);
    const [materialList, setMaterialList] = useState<string[]>([]);
    const [standardsList, setStandardsList] = useState<string[]>([]);
    const [sizeList, setSizeList] = useState<string[]>([]);
    const [shapeList, setShapeList] = useState<string[]>([]);

    // Filtered options states
    const [filteredColours, setFilteredColours] = useState<string[]>([]);
    const [filteredDesigns, setFilteredDesigns] = useState<string[]>([]);
    const [filteredTypes, setFilteredTypes] = useState<string[]>([]);
    const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
    const [filteredMaterials, setFilteredMaterials] = useState<string[]>([]);
    const [filteredStandards, setFilteredStandards] = useState<string[]>([]);
    const [filteredSizes, setFilteredSizes] = useState<string[]>([]);
    const [filteredShapes, setFilteredShapes] = useState<string[]>([]);

    // Pagination and other states
    const [productItemCount, setProductItemCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const itemsPerPage = 9;

    const anchorRef = useRef<HTMLDivElement>(null);
    const debouncedApiCall = useRef<NodeJS.Timeout | null>(null);

    const makeApiCall = useCallback(async (requestData: MultiFilterMutationRequest): Promise<MultiFilterApiResponse> => {
        try {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            const response = await apiFilterProducts(requestData);
            return response;
        } catch (error: any) {
            if (error.name === 'AbortError') {
                throw error;
            }

            // 🔥 REMOVED: No more retry logic - let it fail and show error
            console.error('API call failed:', error);
            throw error;
        }
    }, []);

    // Function to clean up invalid selections (post-API cleanup)
    const cleanUpInvalidSelections = useCallback((filters: FiltersData) => {
        let hasChanges = false;

        const cleanList = (
          currentList: string[],
          availableOptions: string[],
          setList: React.Dispatch<React.SetStateAction<string[]>>,
          listName: string
        ) => {
            if (!availableOptions || availableOptions.length === 0) return currentList;

            const validItems = currentList.filter(item =>
              availableOptions.some(option =>
                option.toLowerCase() === item.toLowerCase()
              )
            );

            if (validItems.length !== currentList.length) {
                console.log(`🧹 Post-API cleanup ${listName}:`, {
                    before: currentList,
                    after: validItems,
                    removed: currentList.filter(item => !validItems.includes(item))
                });
                setList(validItems);
                hasChanges = true;
                return validItems;
            }
            return currentList;
        };

        // Clean each filter category
        const cleanedColours = cleanList(colourList, filters?.web_colour || [], setColourList, 'colours');
        const cleanedDesigns = cleanList(designList, filters?.design_style || [], setDesignList, 'designs');
        const cleanedTypes = cleanList(typesList, filters?.web_type || [], setTypesList, 'types');
        const cleanedBrands = cleanList(brandList, filters?.brand || [], setBrandList, 'brands');

        const cleanedMaterials = cleanList(
          materialList,
          (filters?.web_materials || []).filter((material: string) => material !== "-"),
          setMaterialList,
          'materials'
        );
        const cleanedStandards = cleanList(
          standardsList,
          (filters?.web_standards || []).filter((standard: string) => standard !== "-"),
          setStandardsList,
          'standards'
        );

        const cleanedSizes = cleanList(sizeList, filters?.web_size || [], setSizeList, 'sizes');
        const cleanedShapes = cleanList(shapeList, filters?.web_shape || [], setShapeList, 'shapes');

        if (hasChanges) {
            console.log('🔄 Updating URL after post-API cleanup');

            const currentParams = new URLSearchParams();

            applicationsList.forEach((app) => currentParams.append('application', app));
            cleanedColours.forEach((col) => currentParams.append('colours', col));
            cleanedDesigns.forEach((design) => currentParams.append('design_style', design));
            cleanedTypes.forEach((type) => currentParams.append('types', type));
            cleanedBrands.forEach((brand) => currentParams.append('brand', brand));
            cleanedMaterials.forEach((material) => currentParams.append('material', material));
            cleanedStandards.forEach((std) => currentParams.append('standards', std));
            cleanedSizes.forEach((sz) => currentParams.append('sizes', sz));
            cleanedShapes.forEach((shape) => currentParams.append('shapes', shape));

            currentParams.set('page', '1');
            currentParams.set('startoffset', '0');
            currentParams.set('endoffset', itemsPerPage.toString());

            if (typeof window !== "undefined") {
                const newUrl = `${location.pathname}?${currentParams.toString()}`;
                window.history.replaceState({ path: newUrl }, '', newUrl);
            }
        }

        return hasChanges;
    }, [
        colourList, designList, typesList, brandList, materialList, standardsList, sizeList, shapeList,
        setColourList, setDesignList, setTypesList, setBrandList, setMaterialList, setStandardsList, setSizeList, setShapeList,
        applicationsList, location.pathname, itemsPerPage
    ]);

    const getProductsMutation: MultiFilterMutation = useMutation<MultiFilterApiResponse, Error, MultiFilterMutationRequest>(
      makeApiCall,
      {
          onSuccess: (response) => {
              console.log('✅ Multi-Filter Mutation Success:', response);

              const filters = response.filters;

              // 🔥 SIMPLIFIED: React 18 automatically batches these updates
              setAvailableApplications(filters?.application || []);
              setAvailableColours(filters?.web_colour || []);
              setAvailableDesigns(filters?.design_style || []);
              setAvailableTypes(filters?.web_type || []);
              setAvailableBrands(filters?.brand || []);
              setAvailableMaterials(filters?.web_materials?.filter((material: string) => material !== "-") || []);
              setAvailableStandards(filters?.web_standards?.filter((standard: string) => standard !== "-") || []);
              setAvailableSizes(filters?.web_size || []);
              setAvailableShapes(filters?.web_shape || []);

              setFilteredColours(filters?.web_colour || []);
              setFilteredDesigns(filters?.design_style || []);
              setFilteredTypes(filters?.web_type || []);
              setFilteredBrands(filters?.brand || []);
              setFilteredMaterials(filters?.web_materials?.filter((material: string) => material !== "-") || []);
              setFilteredStandards(filters?.web_standards?.filter((standard: string) => standard !== "-") || []);
              setFilteredSizes(filters?.web_size || []);
              setFilteredShapes(filters?.web_shape || []);


              setProductItemCount(response.total_items_count);
              setTotalPages(Math.ceil(response.total_items_count / itemsPerPage));
          },
          onError: (error: Error) => {
              if (error.name !== 'AbortError') {
                  console.error("Error fetching products:", error);
              }
          }
      }
    );

    const applyFiltersImmediately = useCallback((
      updatedColours?: string[],
      updatedDesigns?: string[],
      updatedTypes?: string[],
      updatedBrands?: string[],
      updatedMaterials?: string[],
      updatedStandards?: string[],
      updatedSizes?: string[],
      updatedShapes?: string[],
      updatedApplications?: string[]
    ) => {
        // Clear existing debounce timeout
        if (debouncedApiCall.current) {
            clearTimeout(debouncedApiCall.current);
        }

        // Use provided values or current state
        const applicationsToUse = updatedApplications || applicationsList;
        const coloursToUse = updatedColours !== undefined ? updatedColours : colourList;
        const designsToUse = updatedDesigns !== undefined ? updatedDesigns : designList;
        const typesToUse = updatedTypes !== undefined ? updatedTypes : typesList;
        const brandsToUse = updatedBrands !== undefined ? updatedBrands : brandList;
        const materialsToUse = updatedMaterials !== undefined ? updatedMaterials : materialList;
        const standardsToUse = updatedStandards !== undefined ? updatedStandards : standardsList;
        const sizesToUse = updatedSizes !== undefined ? updatedSizes : sizeList;
        const shapesToUse = updatedShapes !== undefined ? updatedShapes : shapeList;

        // 🔥 REMOVED: Conflicting filter logic that causes cascading state updates
        let finalColours = coloursToUse;
        let finalBrands = brandsToUse;

        // Create URL params
        const currentParams = new URLSearchParams();
        applicationsToUse.forEach((app) => currentParams.append('application', app));
        finalColours.forEach((col) => currentParams.append('colours', col));
        designsToUse.forEach((design) => currentParams.append('design_style', design));
        typesToUse.forEach((type) => currentParams.append('types', type));
        finalBrands.forEach((brand) => currentParams.append('brand', brand));
        materialsToUse.forEach((material) => currentParams.append('material', material));
        standardsToUse.forEach((std) => currentParams.append('standards', std));
        sizesToUse.forEach((sz) => currentParams.append('sizes', sz));
        shapesToUse.forEach((shape) => currentParams.append('shapes', shape));

        currentParams.set('page', '1');
        currentParams.set('startoffset', '0');
        currentParams.set('endoffset', itemsPerPage.toString());

        // Update URL immediately
        if (typeof window !== "undefined") {
            const newUrl = `${location.pathname}?${currentParams.toString()}`;
            window.history.replaceState({ path: newUrl }, '', newUrl);
        }

        // 🔥 DEBOUNCE: Wait 300ms before making API call
        debouncedApiCall.current = setTimeout(() => {
            const filterObject: MultiFilterMutationRequest['filters'] = {
                item_group: itemGroup
            };

            if (applicationsToUse.length > 0) filterObject.application = applicationsToUse;
            if (finalColours.length > 0) filterObject.web_colour = finalColours;
            if (designsToUse.length > 0) filterObject.design_style = designsToUse;
            if (finalBrands.length > 0) filterObject.brand = finalBrands;
            if (materialsToUse.length > 0) filterObject.web_materials = materialsToUse;
            if (standardsToUse.length > 0) filterObject.web_standards = standardsToUse;
            if (sizesToUse.length > 0) filterObject.web_size = sizesToUse;
            if (shapesToUse.length > 0) filterObject.web_shape = shapesToUse;
            if (typesToUse.length > 0) filterObject.web_type = typesToUse;

            console.log('🚀 Debounced API Request:', { filters: filterObject });

            getProductsMutation.mutate({
                filters: filterObject,
                filter_fields: [
                    "application", "item_group", "brand", "web_colour", "design_style",
                    "web_materials", "web_shape", "web_size", "web_type", "web_standards"
                ],
                main_filter_field: null,
                selection_fields: null,
                sort_by_setting: "Alphabetical A-Z",
                off_setter: { start: 0, end: itemsPerPage },
                only_published: true,
                only_promo: false,
                only_in_stock: false,
                limited: true
            });
        }, 300); // 300ms debounce

        setCurrentPage(1);
    }, [
        applicationsList, colourList, designList, typesList, brandList, materialList,
        standardsList, sizeList, shapeList, itemGroup, location.pathname,
        itemsPerPage, getProductsMutation
    ]);

    // Updated handleCheckboxChange for immediate filtering
    const handleCheckboxChange = (
      elem: string,
      setList: React.Dispatch<React.SetStateAction<string[]>>,
      list: string[],
      paramKey: string
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        let updatedList: string[];
        if (isChecked) {
            updatedList = [...list, elem];
        } else {
            updatedList = list.filter((item) => item !== elem);
        }

        setList(updatedList);

        // Apply filters immediately based on which filter was changed
        switch (paramKey) {
            case 'colours':
                applyFiltersImmediately(updatedList);
                break;
            case 'design_style':
                applyFiltersImmediately(undefined, updatedList);
                break;
            case 'types':
                applyFiltersImmediately(undefined, undefined, updatedList);
                break;
            case 'brand':
                applyFiltersImmediately(undefined, undefined, undefined, updatedList);
                break;
            case 'material':
                applyFiltersImmediately(undefined, undefined, undefined, undefined, updatedList);
                break;
            case 'standards':
                applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, updatedList);
                break;
            case 'sizes':
                applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, updatedList);
                break;
            case 'shapes':
                applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedList);
                break;
        }
    };

    // Filter tag removal handler
    const handleRemoveFilter = (category: string, value: string) => {
        switch (category) {
            case 'colours':
                const newColours = colourList.filter(item => item !== value);
                setColourList(newColours);
                applyFiltersImmediately(newColours);
                break;
            case 'design_style':
                const newDesigns = designList.filter(item => item !== value);
                setDesignList(newDesigns);
                applyFiltersImmediately(undefined, newDesigns);
                break;
            case 'types':
                const newTypes = typesList.filter(item => item !== value);
                setTypesList(newTypes);
                applyFiltersImmediately(undefined, undefined, newTypes);
                break;
            case 'brand':
                const newBrands = brandList.filter(item => item !== value);
                setBrandList(newBrands);
                applyFiltersImmediately(undefined, undefined, undefined, newBrands);
                break;
            case 'material':
                const newMaterials = materialList.filter(item => item !== value);
                setMaterialList(newMaterials);
                applyFiltersImmediately(undefined, undefined, undefined, undefined, newMaterials);
                break;
            case 'standards':
                const newStandards = standardsList.filter(item => item !== value);
                setStandardsList(newStandards);
                applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, newStandards);
                break;
            case 'sizes':
                const newSizes = sizeList.filter(item => item !== value);
                setSizeList(newSizes);
                applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, newSizes);
                break;
            case 'shapes':
                const newShapes = shapeList.filter(item => item !== value);
                setShapeList(newShapes);
                applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, newShapes);
                break;
        }
    };

    // Clear all filters handler
    const handleClearAllFilters = () => {
        setColourList([]);
        setDesignList([]);
        setTypesList([]);
        setBrandList([]);
        setMaterialList([]);
        setStandardsList([]);
        setSizeList([]);
        setShapeList([]);

        applyFiltersImmediately([], [], [], [], [], [], [], []);
    };

    // Function to immediately apply filters when applications change
    const applyFiltersForApplications = (updatedApplications: string[]) => {
        saveCurrentPosition();
        applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedApplications);
    };

    // Updated getInitialData function
    const getInitialData = () => {
        const currentParams = new URLSearchParams(location.search);

        // Get all parameters from URL
        const applicationParams = currentParams.getAll('application');
        const colourParams = currentParams.getAll('colours');
        const designParams = currentParams.getAll('design_style');
        const typeParams = currentParams.getAll('types');
        const brandParams = currentParams.getAll('brand');
        const materialParams = currentParams.getAll('material');
        const standardsParams = currentParams.getAll('standards');
        const sizeParams = currentParams.getAll('sizes');
        const shapeParams = currentParams.getAll('shapes');

        // If no application params but we have defaults, use the defaults
        const applicationsToUse = applicationParams.length > 0
          ? applicationParams
          : defaultApplications;

        // Set states based on URL parameters or defaults
        setApplicationsList(applicationsToUse);
        setColourList(colourParams);
        setDesignList(designParams);
        setTypesList(typeParams);
        setBrandList(brandParams);
        setMaterialList(materialParams);
        setStandardsList(standardsParams);
        setSizeList(sizeParams);
        setShapeList(shapeParams);

        // Get pagination parameters
        const page = parseInt(currentParams.get('page') || '1');
        const startOffset = parseInt(currentParams.get('startoffset') || '0');
        const endOffset = parseInt(currentParams.get('endoffset') || itemsPerPage.toString());

        // 🔥 UPDATED: Create the filter object for the multi-filter API
        const filterObject: MultiFilterMutationRequest['filters'] = {
            item_group: itemGroup
        };

        // Only add non-empty arrays to the filter
        if (applicationsToUse.length > 0) filterObject.application = applicationsToUse;
        if (colourParams.length > 0) filterObject.web_colour = colourParams;
        if (designParams.length > 0) filterObject.design_style = designParams;
        if (typeParams.length > 0) filterObject.web_type = typeParams;
        if (brandParams.length > 0) filterObject.brand = brandParams;
        if (materialParams.length > 0) filterObject.web_materials = materialParams;
        if (standardsParams.length > 0) filterObject.web_standards = standardsParams;
        if (sizeParams.length > 0) filterObject.web_size = sizeParams;
        if (shapeParams.length > 0) filterObject.web_shape = shapeParams;

        // 🔥 UPDATED: Fetch data with the new multi-filter structure
        getProductsMutation.mutate({
            filters: filterObject,
            filter_fields: [
                "application",
                "item_group",
                "brand",
                "web_colour",
                "design_style",
                "web_materials",
                "web_shape",
                "web_size",
                "web_type",
                "web_standards"
            ],
            main_filter_field: null,
            selection_fields: null,
            sort_by_setting: "Alphabetical A-Z",
            off_setter: {
                start: startOffset,
                end: endOffset,
            },
            only_published: true,
            only_promo: false,
            only_in_stock: false,
            limited: true
        });

        setCurrentPage(page);
    };

    // Updated clearFilters function
    const clearFilters = () => {
        // Determine which applications to keep
        const currentParams = new URLSearchParams(location.search);
        const currentApplication = currentParams.get('application');

        const isFabricApplication = currentApplication &&
          ['Curtaining', 'Upholstery', 'Dual Purpose'].includes(currentApplication);

        const applicationsToKeep = isFabricApplication
          ? defaultApplications
          : currentApplication
            ? [currentApplication]
            : [];

        setApplicationsList(applicationsToKeep);
        handleClearAllFilters();

        // Update URL
        const params = new URLSearchParams();
        applicationsToKeep.forEach(app => params.append('application', app));

        if (typeof window !== "undefined") {
            const newUrl = `${location.pathname}${applicationsToKeep.length ? '?' + params.toString() : ''}`;
            window.history.replaceState({ path: newUrl }, '', newUrl);
        }

        setCurrentPage(1);
        scrollToFilterComponent();
    };

    // Helper function to save current position
    const saveCurrentPosition = () => {
        const currentPosition = window.scrollY || document.documentElement.scrollTop;
        sessionStorage.setItem('multifilter_scroll_position', currentPosition.toString());
        sessionStorage.setItem('multifilter_item_group', itemGroup);
        sessionStorage.setItem('multifilter_active', 'true');

        if (location.pathname.includes('/item_group/') && !location.pathname.includes('/application/')) {
            sessionStorage.setItem('changing_from_category_to_application', 'true');
        }
    };

    const scrollToFilterComponent = () => {
        requestAnimationFrame(() => {
            if (anchorRef.current) {
                const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                const rect = anchorRef.current.getBoundingClientRect();
                const absoluteTop = rect.top + currentScrollPosition;
                const scrollToPosition = absoluteTop - 80;

                window.scrollTo({
                    top: scrollToPosition,
                    behavior: 'smooth'
                });
            }
        });
    };

    // 🔥 UPDATED: Pagination functions with new multi-filter structure
    const createPaginationRequest = (startOffset: number, endOffset: number): MultiFilterMutationRequest => {
        const filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
        if (applicationsList.length > 0) filterObject.application = applicationsList;
        if (colourList.length > 0) filterObject.web_colour = colourList;
        if (designList.length > 0) filterObject.design_style = designList;
        if (typesList.length > 0) filterObject.web_type = typesList;
        if (brandList.length > 0) filterObject.brand = brandList;
        if (materialList.length > 0) filterObject.web_materials = materialList;
        if (standardsList.length > 0) filterObject.web_standards = standardsList;
        if (sizeList.length > 0) filterObject.web_size = sizeList;
        if (shapeList.length > 0) filterObject.web_shape = shapeList;

        return {
            filters: filterObject,
            filter_fields: [
                "application", "item_group", "brand", "web_colour", "design_style",
                "web_materials", "web_shape", "web_size", "web_type", "web_standards"
            ],
            main_filter_field: null,
            selection_fields: null,
            sort_by_setting: "Alphabetical A-Z",
            off_setter: { start: startOffset, end: endOffset },
            only_published: true,
            only_promo: false,
            only_in_stock: false,
            limited: true
        };
    };

    // 🔥 UPDATED: Pagination functions with new multi-filter structure
    const nextPage = () => {
        if (currentPage === totalPages) return;

        saveCurrentPosition();

        const nextPageNum = currentPage + 1;
        const startOffset = (nextPageNum - 1) * itemsPerPage;
        const endOffset = startOffset + itemsPerPage;

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('page', nextPageNum.toString());
        currentParams.set('startoffset', startOffset.toString());
        currentParams.set('endoffset', endOffset.toString());
        const newUrl = `${location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);

        setCurrentPage(nextPageNum);
        scrollToFilterComponent();

        getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
    };

    const previousPage = () => {
        if (currentPage === 1) return;

        saveCurrentPosition();

        const prevPageNum = currentPage - 1;
        const startOffset = (prevPageNum - 1) * itemsPerPage;
        const endOffset = startOffset + itemsPerPage;

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('page', prevPageNum.toString());
        currentParams.set('startoffset', startOffset.toString());
        currentParams.set('endoffset', endOffset.toString());
        const newUrl = `${location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);

        setCurrentPage(prevPageNum);
        scrollToFilterComponent();

        getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
    };

    const lastPage = () => {
        if (currentPage === totalPages) return;

        saveCurrentPosition();

        const lastPageNumber = totalPages;
        const startOffset = (lastPageNumber - 1) * itemsPerPage;
        const endOffset = startOffset + itemsPerPage;

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('page', lastPageNumber.toString());
        currentParams.set('startoffset', startOffset.toString());
        currentParams.set('endoffset', endOffset.toString());
        const newUrl = `${location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);

        setCurrentPage(lastPageNumber);
        scrollToFilterComponent();

        getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
    };

    const firstPage = () => {
        if (currentPage === 1) return;

        saveCurrentPosition();

        const firstPageNumber = 1;
        const startOffset = 0;
        const endOffset = itemsPerPage;

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('page', firstPageNumber.toString());
        currentParams.set('startoffset', startOffset.toString());
        currentParams.set('endoffset', endOffset.toString());

        const newUrl = `${location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);

        setCurrentPage(firstPageNumber);
        scrollToFilterComponent();

        getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
    };

// Run getInitialData when the component mounts or when location.search changes
    useEffect(() => {
        getInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

// Add this useEffect for default applications
    useEffect(() => {
        const defaults = getDefaultApplications(location.pathname);
        setDefaultApplications(defaults);

        // If this is initial load (no search params) and we have defaults, set them
        if (!location.search && defaults.length > 0) {
            setApplicationsList(defaults);

            // Update URL to include default applications
            const params = new URLSearchParams();
            defaults.forEach((app: string) => params.append('application', app));
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });

            // 🔥 UPDATED: Create filter object with default applications for multi-filter
            const filterObject: MultiFilterMutationRequest['filters'] = {
                item_group: itemGroup
            };

            if (defaults.length > 0) filterObject.application = defaults;

            // 🔥 UPDATED: Fetch products with default applications using the new multi-filter format
            getProductsMutation.mutate({
                filters: filterObject,
                filter_fields: [
                    "application",
                    "item_group",
                    "brand",
                    "web_colour",
                    "design_style",
                    "web_materials",
                    "web_shape",
                    "web_size",
                    "web_type",
                    "web_standards"
                ],
                main_filter_field: null,
                selection_fields: null,
                sort_by_setting: "Alphabetical A-Z",
                off_setter: {
                    start: 0,
                    end: itemsPerPage,
                },
                only_published: true,
                only_promo: false,
                only_in_stock: false,
                limited: true
            });
        }
    }, [location.pathname, navigate, itemGroup]);


    useEffect(() => {
        return () => {
            // Cleanup debounce timeout on unmount
            if (debouncedApiCall.current) {
                clearTimeout(debouncedApiCall.current);
            }

            // Abort any ongoing requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
      <Container maxWidth="xl">
          <Typography
            variant="h3"
            mb={1}
            mt={10}
            ml={2}
            fontWeight={400}
            fontSize={'40px'}
            textTransform={'capitalize'}
          >
              {`Discover - ${title}`}
          </Typography>
          <Typography fontWeight={400} fontSize="18px" mb={'-4rem'} ml={2}>
              Our inventory features thousands of designs. Use the selection bar below to find exactly what you're looking for.
          </Typography>

          <Box ref={anchorRef} id="filter-component-section">
              <Grid
                container
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    height: 'auto',
                    width: '100%',
                    padding: '1rem',
                    m: '4rem 0',
                    minHeight: {
                        xs: '2250px',
                        sm: '1500px',
                        lg: '1300px'
                    },
                    contain: 'layout',
                    transition: 'min-height 0.3s ease-in-out'
                }}
              >
                  <Box
                    sx={{
                        flex: { xs: 12, lg: 4 },
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        mr: { xs: 0, lg: 5 },
                    }}
                  >
                      <ClearFilters clearFilters={clearFilters} />

                      {availableApplications.length > 1 && (
                        <ApplicationSelection
                          availableApplications={availableApplications}
                          handleCheckboxChange={handleCheckboxChange}
                          applicationsList={applicationsList}
                          setApplicationsList={setApplicationsList}
                          itemGroup={itemGroup}
                          applyFiltersForApplications={applyFiltersForApplications}
                        />
                      )}

                      <FilterSelection
                        // Available options
                        availableColours={availableColours}
                        availableDesigns={availableDesigns}
                        availableTypes={availableTypes}
                        availableBrands={availableBrands}
                        availableMaterials={availableMaterials}
                        availableStandards={availableStandards}
                        availableSizes={availableSizes}
                        availableShapes={availableShapes}

                        // Filtered options
                        filteredColours={filteredColours}
                        filteredDesigns={filteredDesigns}
                        filteredTypes={filteredTypes}
                        filteredBrands={filteredBrands}
                        filteredMaterials={filteredMaterials}
                        filteredStandards={filteredStandards}
                        filteredSizes={filteredSizes}
                        filteredShapes={filteredShapes}

                        // Selected values
                        colourList={colourList}
                        designList={designList}
                        typesList={typesList}
                        brandList={brandList}
                        materialList={materialList}
                        standardsList={standardsList}
                        sizeList={sizeList}
                        shapeList={shapeList}

                        // State setters
                        setColourList={setColourList}
                        setDesignList={setDesignList}
                        setTypesList={setTypesList}
                        setBrandList={setBrandList}
                        setMaterialList={setMaterialList}
                        setStandardsList={setStandardsList}
                        setSizeList={setSizeList}
                        setShapeList={setShapeList}

                        // Other props
                        handleCheckboxChange={handleCheckboxChange}
                        applicationsList={applicationsList}
                        itemGroup={itemGroup}
                        isLoading={getProductsMutation.isLoading}
                      />
                  </Box>

                  <Box
                    flex={{ xs: 12, lg: 8 }}
                    sx={{
                        height: '100%',
                        flexDirection: 'column',
                    }}
                  >
                      {/* Filter Tags Component */}
                      <FilterTags
                        colourList={colourList}
                        designList={designList}
                        typesList={typesList}
                        brandList={brandList}
                        materialList={materialList}
                        standardsList={standardsList}
                        sizeList={sizeList}
                        shapeList={shapeList}
                        onRemoveFilter={handleRemoveFilter}
                        onClearAll={handleClearAllFilters}
                      />

                      {/* 🔥 FIXED: Use .products instead of .data */}
                      {getProductsMutation?.data?.products?.length === 0 || productItemCount === 0 ? (
                        <Box
                          sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexDirection: 'column',
                              width: '100%',
                              height: '80vh',
                          }}
                        >
                            <Typography fontSize={'1.5rem'} fontWeight={'bold'}>
                                No items found
                            </Typography>
                            <Typography fontSize={'1.5rem'} fontWeight={'bold'}>
                                Please try a different selection
                            </Typography>
                        </Box>
                      ) : (
                        <ProductGrid
                          productItemCount={productItemCount}
                          getProductsMutation={getProductsMutation}
                          totalPages={totalPages}
                          currentPage={currentPage}
                          previousPage={previousPage}
                          nextPage={nextPage}
                          lastPage={lastPage}
                          firstPage={firstPage}
                          anchorRef={anchorRef}
                        />
                      )}
                  </Box>
              </Grid>
          </Box>
      </Container>
    );

}

export default FilterComponent;